import React, { useState, useEffect, useMemo } from "react";
import { Divider, Backdrop, CircularProgress, FormLabel, FormControlLabel, RadioGroup, Radio, TextField, Dialog, DialogActions, DialogTitle, DialogContent, Button, Box, Typography, IconButton, FormControl } from "@mui/material";
import DeleteForever from "@mui/icons-material/DeleteForever";
import FileUpload from "@mui/icons-material/FileUpload";
import CampaignIcon from "@mui/icons-material/Campaign";
import { useAlert } from "../AlertProvider";
import { DropArea, ProgressBar, StyledDialog, StyledPaper } from "../StyledMui";
import useStore from "../Store";
import * as Utils from "../Utils";
import ApiRequest from "../ApiRequest";

function NewImageDialog() {
  const { BASIC_ALERT_DURATION, getImages, newImageDialog, closeNewImageDialog, onLoading, offLoading } = useStore((state) => state.header);
  const { showAlert } = useAlert();
  const [imageName, setImageName] = useState("");
  const [messageType, setMessageType] = useState("alimtalk");
  const [imageType, setImageType] = useState("template");
  const [maxImageCount, setMaxImageCount] = useState(messageType === "friendtalk" && imageType === "wide_item_list" ? 4 : messageType === "friendtalk" && imageType === "carousel_feed" ? 6 : 1);
  const [imageFiles, setImageFiles] = useState(Array.from({ length: maxImageCount }, () => null));
  const [loadingModal, setLoadingModal] = useState(false);

  useEffect(() => {
    if (messageType === "friendtalk") {
      setImageType("default");
    } else {
      setImageType("template");
    }
  }, [messageType]);

  useEffect(() => {
    const newMaxImageCount = messageType === "friendtalk" && imageType === "wide_item_list" ? 4 : messageType === "friendtalk" && imageType === "carousel_feed" ? 6 : 1;
    setMaxImageCount(newMaxImageCount);
    setImageFiles((prevImageFiles) => Array.from({ length: newMaxImageCount }, (_, i) => prevImageFiles[i] || null));
  }, [messageType, imageType]);

  const initializeState = () => {
    setImageName("");
    setMessageType("alimtalk");
    setImageType("template");
    setImageFiles(Array.from({ length: maxImageCount }, () => null));
  };

  const handleMessageTypeChange = (event) => {
    setMessageType(event.target.value);
    setImageFiles(Array.from({ length: imageFiles.length }, () => null));
  };

  const handleImageTypeChange = (event) => {
    setImageType(event.target.value);
    setImageFiles(Array.from({ length: imageFiles.length }, () => null));
  };

  const handleImageNameChange = (event) => {
    setImageName(event.target.value);
  };

  const handleImageFileChange = async (e, index) => {
    console.log(index);
    console.log(e.target);
    const file = e.target.files[0];
    let validationResult;
    if (file) {
      validationResult = await validateImageFile(file);
      console.log(validationResult);
    }
    if (validationResult.valid) {
      const newImageFiles = [...imageFiles];
      newImageFiles[index] = file;
      setImageFiles(newImageFiles);
    }
    if (validationResult.type) {
      return showAlert(validationResult.message, validationResult.type, BASIC_ALERT_DURATION);
    }
  };

  const handleDeleteImageClick = (index) => {
    const newImageFiles = [...imageFiles];
    newImageFiles[index] = null;
    setImageFiles(newImageFiles);
  };

  const handleDrop = (e, index) => {
    e.preventDefault();
    const newImageFiles = [...imageFiles];
    newImageFiles[index] = e.dataTransfer.files[0];
    setImageFiles(newImageFiles);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const imageCountValidation = () => {
    const acceptableEmptyIndex = imageType === "wide_item_list" ? 3 : imageType === "carousel_feed" ? 2 : imageFiles.length;
    const firstNullIndex = imageFiles.slice(0, acceptableEmptyIndex).findIndex((file) => file === null);

    if (firstNullIndex !== -1 && imageFiles.slice(firstNullIndex + 1).some((file) => file !== null)) {
      return false;
    }

    if (imageType === "wide_item_list") {
      return imageFiles.slice(0, maxImageCount).filter((file) => file !== null).length >= 3;
    } else if (imageType === "carousel_feed") {
      return imageFiles.slice(0, maxImageCount).filter((file) => file !== null).length >= 2;
    } else {
      return imageFiles.every((file) => file !== null);
    }
  };

  const validateImageFile = async (imageFile) => {
    let recommendedSize, restrictedSize, maxFileSize;
    let allowedTypes = ["image/jpeg", "image/png"];
    let imageSize = await Utils.getImageDimensions(imageFile);
    let imageRatio = imageSize.width / imageSize.height;
    let allowedRatio;
    console.log(imageSize);
    console.log("사이즈");

    if (messageType === "friendtalk") {
      if (imageType === "default") {
        recommendedSize = { width: 720, height: 720 };
        allowedRatio = 1;
        restrictedSize = { minWidth: 500, minHeight: null, minRatio: 0.75, maxRatio: 2 };
        restrictedSize = { minWidth: 500, minHeight: null };
        maxFileSize = 500 * 1024;
      } else if (imageType === "wide_balloon") {
        recommendedSize = null;
        restrictedSize = { width: 800, height: 600 };
        maxFileSize = 2 * 1024 * 1024;
      } else if (imageType === "wide_item_list") {
        recommendedSize = null;
        restrictedSize = { minWidth: 400, maxWidth: 800, height: 400 };
        maxFileSize = 2 * 1024 * 1024;
      } else if (imageType === "carousel_feed") {
        recommendedSize = null;
        restrictedSize = { minWidth: 500, minHeight: null, minRatio: 0.75, maxRatio: 2 };
        restrictedSize = { minWidth: 500, minHeight: null };
        maxFileSize = 2 * 1024 * 1024;
      }
    } else if (messageType === "alimtalk") {
      if (imageType === "template") {
        recommendedSize = { width: 800, height: 400 };
        restrictedSize = { minWidth: 500, minHeight: null, ratio: 2 };
        maxFileSize = 500 * 1024;
      } else if (imageType === "highlight") {
        recommendedSize = { width: 250, height: 250 };
        restrictedSize = { minWidth: 108, minHeight: null, ratio: 1 };
        maxFileSize = 500 * 1024;
      }
    }

    if (restrictedSize) {
      if ((restrictedSize.minWidth && imageSize.width < restrictedSize.minWidth) || (restrictedSize.maxWidth && imageSize.width > restrictedSize.maxWidth) || (restrictedSize.minHeight && imageSize.height < restrictedSize.minHeight) || (restrictedSize.maxHeight && imageSize.height > restrictedSize.maxHeight) || (restrictedSize.height && imageSize.height !== restrictedSize.height)) {
        return {
          valid: false,
          message: `이미지 사이즈가 제한 사이즈를 벗어났습니다.`,
          type: "error",
        };
      }

      if (!(allowedRatio && imageRatio === allowedRatio) && ((restrictedSize.minRatio && imageRatio < restrictedSize.minRatio) || (restrictedSize.maxRatio && imageRatio > restrictedSize.maxRatio))) {
        return {
          valid: false,
          message: `이미지 비율이 제한 비율을 벗어났습니다.`,
          type: "error",
        };
      }
    }

    if (imageFile.size > maxFileSize) {
      return {
        valid: false,
        message: `파일 크기가 제한사이즈 보다 큽니다. (최대 ${maxFileSize / 1024}KB까지 가능합니다)`,
        type: "error",
      };
    }
    if (!allowedTypes.includes(imageFile.type)) {
      return {
        valid: false,
        message: "올바른 파일 형식(jpg, png)이 아닙니다.",
        type: "error",
      };
    }

    if (recommendedSize && (imageSize.width !== recommendedSize.width || imageSize.height !== recommendedSize.height)) {
      return {
        valid: true,
        message: `이미지 사이즈가 권장 사이즈(${recommendedSize.width}*${recommendedSize.height})와 일치하지 않습니다.`,
        type: "warning",
      };
    }

    return { valid: true, message: "이미지가 정상적으로 업로드 되었습니다.", type: "success" }; // 모든 검사를 통과한 경우
  };

  const handleSaveButtonClick = async () => {
    let endPoint = "";

    if (messageType === "friendtalk") {
      if (imageType === "default") {
        endPoint = "/biztalkCenterApi/image/friendtalk";
      } else if (imageType === "wide_balloon") {
        endPoint = "/biztalkCenterApi/image/friendtalk/wide";
      } else if (imageType === "wide_item_list") {
        endPoint = "/biztalkCenterApi/image/friendtalk/wide/item_list";
      } else if (imageType === "carousel_feed") {
        endPoint = "/biztalkCenterApi/image/friendtalk/carousel/upload_image";
      }
    } else if (messageType === "alimtalk") {
      if (imageType === "template") {
        endPoint = "/biztalkCenterApi/image/alimtalk/template";
      } else if (imageType === "highlight") {
        endPoint = "/biztalkCenterApi/image/alimtalk/itemHighlight";
      }
    }

    const formData = new FormData();
    console.log("입력한 이미지 명 : " + imageName);
    console.log(imageFiles);
    formData.append("name", imageName);
    if (imageFiles.length === 1) {
      formData.append("image", imageFiles[0]);
    } else {
      imageFiles.forEach((imageFile, index) => {
        console.log(imageFile);
        if (imageFile) {
          formData.append(`image_${index + 1}`, imageFile);
        }
      });
    }
    console.log(formData);

    try {
      setLoadingModal(true);
      const apiResponse = await ApiRequest.post(endPoint, formData, {
        headers: {
          "Content-Type": `multipart/form-data; boundary=${formData._boundary}`,
        },
      });
      console.log(JSON.stringify(apiResponse, null, 2));

      let insertTarget = {};
      if (apiResponse.result !== undefined && apiResponse.result.success !== undefined) {
        let imageUrls = apiResponse.result.success.map((img) => img.url);

        insertTarget = {
          tableName: "image",
          data: {
            imageUrl: JSON.stringify(imageUrls),
            name: imageName,
            type: messageType,
            subType: imageType,
          },
        };
      } else if (apiResponse.image !== undefined) {
        insertTarget = {
          tableName: "image",
          data: {
            imageUrl: JSON.stringify([apiResponse.image]),
            name: imageName,
            type: messageType,
            subType: imageType,
          },
        };
      } else {
      }

      console.log(insertTarget);
      const dbResponse = await ApiRequest.post("/db/insertData", insertTarget);
      console.log("dbResponse : " + dbResponse);
      closeNewImageDialog();
      getImages();
      initializeState();
      showAlert("이미지가 성공적으로 저장되었습니다.", "success", BASIC_ALERT_DURATION);
    } catch (error) {
      console.error("API 호출 오류:", error);
    } finally {
      setLoadingModal(false);
    }
  };

  const handleCancelButtonClick = () => {
    closeNewImageDialog();
    setImageFiles(Array.from({ length: imageFiles.length }, () => null));
  };

  return (
    <Dialog maxWidth="md" fullWidth open={newImageDialog} onClose={() => closeNewImageDialog()}>
      <DialogTitle>새로운 이미지 추가</DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
        {loadingModal ? (
          <Backdrop open={true}>
            <CircularProgress color="inherit" />
          </Backdrop>
        ) : (
          <></>
        )}
        {/* 이미지명 인풋창 */}
        <FormControl component="fieldset" margin="normal">
          <FormLabel component="legend">이미지명</FormLabel>
          <TextField placeholder="이미지명을 입력하세요" value={imageName} onChange={handleImageNameChange} focused fullWidth margin="dense" variant="outlined" />
        </FormControl>
        {/* 메시지 유형 라디오 버튼 */}
        <FormControl component="fieldset" margin="normal">
          <FormLabel component="legend">메세지 유형</FormLabel>
          <RadioGroup row name="messageType" value={messageType} onChange={handleMessageTypeChange}>
            <FormControlLabel value="alimtalk" control={<Radio />} label="알림톡" />
            <FormControlLabel value="friendtalk" control={<Radio />} label="친구톡" />
          </RadioGroup>
        </FormControl>
        {/* 구분 라디오 버튼 */}
        {messageType === "alimtalk" ? (
          <FormControl component="fieldset" margin="normal">
            <FormLabel component="legend">구분</FormLabel>
            <RadioGroup row name="imageType" value={imageType} onChange={handleImageTypeChange}>
              <FormControlLabel value="template" control={<Radio />} label="템플릿 이미지" />
              <FormControlLabel value="highlight" control={<Radio />} label="아이템 하이라이트" />
            </RadioGroup>
          </FormControl>
        ) : (
          <FormControl component="fieldset" margin="normal">
            <FormLabel component="legend">구분</FormLabel>
            <RadioGroup row name="imageType" value={imageType} onChange={handleImageTypeChange}>
              <FormControlLabel value="default" control={<Radio />} label="기본" />
              <FormControlLabel value="wide_balloon" control={<Radio />} label="와이드 말풍선" />
              <FormControlLabel value="wide_item_list" control={<Radio />} label="와이드 아이템 리스트" />
              <FormControlLabel value="carousel_feed" control={<Radio />} label="캐러셀 피드" />
            </RadioGroup>
          </FormControl>
        )}
        <Divider />
        <Box mt={2}>
          <Box display="flex" alignItems="center">
            <CampaignIcon />
            <Typography variant="subtitle1" component="span" style={{ marginLeft: "8px" }}>
              이미지 업로드 안내
            </Typography>
          </Box>
          {/* 기본 업로드 가이드 */}
          {messageType === "friendtalk" && imageType === "default" && (
            <Typography variant="body2" style={{ whiteSpace: "pre-line" }}>
              ◼️ 권장 사이즈 : 720*720px
              <br />
              ◼️ 제한 사이즈 : 가로 500px 미만 또는 가로:세로 비율이 2:1 미만 또는 3:4 초과시 업로드 불가
              <br />
              ◼️ 파일 크기 : 최대 500KB
              <br />
              ◼️ 파일 형식 : jpg, png
            </Typography>
          )}
          {/* 와이드 말풍선 업로드 가이드 */}
          {messageType === "friendtalk" && imageType === "wide_balloon" && (
            <Typography variant="body2" style={{ whiteSpace: "pre-line" }}>
              ◼️ 제한 사이즈 : 800*600px <br />
              ◼️ 파일 크기 : 최대 2MB <br />
              ◼️ 파일 형식 : jpg, png
            </Typography>
          )}

          {/* 와이드 아이템 리스트 업로드 가이드 */}
          {messageType === "friendtalk" && imageType === "wide_item_list" && (
            <Typography variant="body2" style={{ whiteSpace: "pre-line" }}>
              ◼️ 아이템 리스트 갯수는 최소 3개부터 최대 4개 <br />
              ◼️ 아이템 리스트 갯수에 맞춰서 image_[1~4] 를 사용해서 업로드 <br />
              ◼️ 제한 사이즈 : 400px*400px ~ 800px*400px <br />
              ◼️ 비율 및 가로 픽셀 확인 X. 썸네일 크기에 맞추어 중앙 기준으로 크롭하여 노출 <br />
              ◼️ 파일 크기 : 최대 2MB <br />
              ◼️ 파일 형식 : jpg, png
            </Typography>
          )}

          {/* 캐러셀 피드 업로드 가이드 */}
          {messageType === "friendtalk" && imageType === "carousel_feed" && (
            <Typography variant="body2" style={{ whiteSpace: "pre-line" }}>
              ◼️ 케러셀 갯수는 최소 2개부터 최대 6개까지 입니다. <br />
              ◼️ 케러셀 리스트 갯수에 맞춰서 image_[1~6] 를 사용해서 업로드 합니다 <br />
              ◼️ 제한 사이즈 : 가로 500px 이상, 가로:세로 비율 2:1 이상 3:4 이하 <br />
              ◼️ 파일 크기 : 최대 2MB <br />
              ◼️ 파일 형식 : jpg, png
            </Typography>
          )}
          {/* 템플릿 업로드 가이드 */}
          {messageType === "alimtalk" && imageType === "template" && (
            <Typography variant="body2" style={{ whiteSpace: "pre-line" }}>
              ◼️ 권장 사이즈 : 800*400px <br />
              ◼️ 제한 사이즈 : 가로:세로 비율이 2:1이 아니거나, 가로 500px 이하일 경우 업로드 불가 <br />
              ◼️ 파일 크기 : 최대 500KB <br />
              ◼️ 파일 형식 : jpg, png
            </Typography>
          )}
          {/* 하이라이트 업로드 가이드 */}
          {messageType === "alimtalk" && imageType === "highlight" && (
            <Typography variant="body2" style={{ whiteSpace: "pre-line" }}>
              ◼️ 권장 사이즈 : 250*250px <br />
              ◼️ 제한 사이즈 : 가로 108px 이상(가로:세로 비율이 1:1만 업로드 가능) <br />
              ◼️ 파일 크기 : 최대 500KB <br />
              ◼️ 파일 형식 : jpg, png
            </Typography>
          )}
        </Box>

        <Box display="flex" flexDirection="row" flexWrap="wrap">
          {imageFiles.map((imageFile, index) => (
            <Box key={index} flexGrow={1}>
              {!imageFile && (
                <DropArea onDrop={(e) => handleDrop(e, index)} onDragOver={handleDragOver}>
                  <React.Fragment>
                    <label htmlFor={`image-file-${index}`}>
                      <input accept="image/jpeg, image/png" id={`image-file-${index}`} type="file" onChange={(e) => handleImageFileChange(e, index)} style={{ display: "none" }} />
                      <Button variant="contained" component="span">
                        <FileUpload />
                        파일 업로드
                      </Button>
                    </label>
                  </React.Fragment>
                </DropArea>
              )}
              {imageFile && (
                <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center">
                  <img src={URL.createObjectURL(imageFile)} alt="Preview" style={{ maxWidth: "100%", maxHeight: 200 }} />
                  <Box mt={1} display="flex" alignItems="center">
                    <IconButton onClick={() => handleDeleteImageClick(index)}>
                      <DeleteForever />
                    </IconButton>
                    <Box ml={1}>
                      <Typography variant="body1">{imageFile.name}</Typography>
                    </Box>
                  </Box>
                </Box>
              )}
            </Box>
          ))}
        </Box>
      </DialogContent>
      <DialogActions>
        {/* "저장" 버튼 */}
        <Button onClick={handleSaveButtonClick} variant="contained" color="primary" disabled={!imageName || !imageCountValidation()}>
          저장
        </Button>
        {/* "취소" 버튼 */}
        <Button onClick={handleCancelButtonClick} variant="contained" color="primary">
          취소
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default NewImageDialog;
